/**
 * Schema Edge Label Component
 *
 * Custom React Flow edge for displaying Neo4j relationships.
 * Shows:
 * - Relationship type as label
 * - Cardinality indicator
 * - Hover tooltip with details
 */

import React, { memo } from 'react';
import {
  EdgeProps,
  EdgeLabelRenderer,
  getBezierPath,
  BaseEdge,
} from '@xyflow/react';

interface SchemaEdgeData {
  relationshipType: string;
  cardinality?: string;
  description?: string;
  sourceColumn?: string;
  targetColumn?: string;
  properties?: Array<{ name: string; type?: string }>;
}

// Cardinality symbols
const CARDINALITY_SYMBOLS: Record<string, string> = {
  'one-to-one': '1:1',
  'one-to-many': '1:N',
  'many-to-one': 'N:1',
  'many-to-many': 'N:N',
  '1:1': '1:1',
  '1:N': '1:N',
  'N:1': 'N:1',
  'N:N': 'N:N',
};

export const SchemaEdgeLabel: React.FC<EdgeProps> = memo(
  ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
    selected,
    markerEnd,
    style,
  }) => {
    const edgeData = data as SchemaEdgeData | undefined;
    const dimmed = (data as { dimmed?: boolean } | undefined)?.dimmed ?? false;

    const [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

    const cardinalitySymbol = edgeData?.cardinality
      ? CARDINALITY_SYMBOLS[edgeData.cardinality] || edgeData.cardinality
      : null;

    return (
      <>
        <BaseEdge
          id={id}
          path={edgePath}
          markerEnd={markerEnd}
          style={{
            ...style,
            stroke: selected ? '#3b82f6' : ((style?.stroke as string) || '#64748b'),
            strokeWidth: selected ? 3 : ((style?.strokeWidth as number) || 2),
          }}
        />
        <EdgeLabelRenderer>
          <div
            className={`
              absolute transform -translate-x-1/2 -translate-y-1/2
              pointer-events-auto nodrag nopan
              transition-all duration-200
              ${selected ? 'scale-110' : ''}
            `}
            style={{
              left: labelX,
              top: labelY,
              opacity: dimmed ? 0.2 : 1,
            }}
          >
            {/* Main label container */}
            <div
              className={`
                group relative px-2 py-1 rounded-md
                text-xs font-medium whitespace-nowrap
                cursor-pointer select-none
                transition-all duration-200
                ${
                  selected
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'bg-white text-neutral-700 shadow border border-neutral-200 hover:bg-neutral-50'
                }
              `}
            >
              {/* Relationship type */}
              <span className="font-mono text-[11px]">
                {edgeData?.relationshipType || 'RELATES_TO'}
              </span>

              {/* Cardinality badge */}
              {cardinalitySymbol && (
                <span
                  className={`
                    ml-1.5 px-1 py-0.5 rounded text-[9px] font-bold
                    ${selected ? 'bg-blue-600' : 'bg-neutral-100 text-neutral-500'}
                  `}
                >
                  {cardinalitySymbol}
                </span>
              )}

              {/* Hover tooltip */}
              {(edgeData?.description ||
                edgeData?.sourceColumn ||
                edgeData?.properties?.length) && (
                <div
                  className={`
                    absolute left-1/2 -translate-x-1/2 bottom-full mb-2
                    px-3 py-2 rounded-lg shadow-xl
                    bg-neutral-900 text-white text-xs
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible
                    transition-all duration-200
                    min-w-[180px] max-w-[280px]
                    z-50
                  `}
                >
                  {/* Description */}
                  {edgeData?.description && (
                    <p className="mb-2">{edgeData.description}</p>
                  )}

                  {/* Column mapping */}
                  {edgeData?.sourceColumn && edgeData?.targetColumn && (
                    <div className="mb-2 font-mono text-[10px] text-neutral-300">
                      {edgeData.sourceColumn} → {edgeData.targetColumn}
                    </div>
                  )}

                  {/* Properties */}
                  {edgeData?.properties && edgeData.properties.length > 0 && (
                    <div>
                      <p className="font-semibold text-neutral-400 mb-1">
                        Properties:
                      </p>
                      <ul className="space-y-0.5">
                        {edgeData.properties.map((prop, idx) => (
                          <li key={idx} className="font-mono text-[10px]">
                            {prop.name}
                            {prop.type && (
                              <span className="text-neutral-400 ml-1">
                                : {prop.type}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Tooltip arrow */}
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full
                    border-8 border-transparent border-t-neutral-900"
                  />
                </div>
              )}
            </div>
          </div>
        </EdgeLabelRenderer>
      </>
    );
  }
);

SchemaEdgeLabel.displayName = 'SchemaEdgeLabel';

export default SchemaEdgeLabel;
